import React from "react";
import Tooltip from "../../Components/Utils/Tooltip/Tooltip";
import { getFileIcon } from "../../Helpers/fileIcon";
import { formatBytes, LibraryFileNode, LibraryFolderNode } from "../../API/Library/LibraryCommands";
import FileMenu from "./FileMenu";
import FolderMenu from "./FolderMenu";
import { FolderIcon } from "./LibraryIcons";
import { formatFullDate, formatRelativeDate, itemCountLabel } from "./libraryUtils";
import styles from "./Library.module.css";

type FileListProps = {
  folders: LibraryFolderNode[];
  files: LibraryFileNode[];
  searchHits: LibraryFileNode[];
  searching: boolean;
  selectedFiles: string[];
  selectedFolders: string[];
  activeFileId: string;
  canEdit: boolean;
  loading?: boolean;
  onToggleFile: (id: string) => void;
  onToggleFolder: (id: string) => void;
  onOpenFolder: (id: string) => void;
  onSelectFile: (file: LibraryFileNode) => void;
  onPreviewFile: (file: LibraryFileNode) => void;
  onRenameFolder: (id: string, title: string) => void;
  onMoveFolder: (id: string, title: string) => void;
  onDeleteFolder: (id: string) => void;
  onMoveFile: (id: string, title: string) => void;
};

const FileList = ({
  folders,
  files,
  searchHits,
  searching,
  selectedFiles,
  selectedFolders,
  activeFileId,
  canEdit,
  loading,
  onToggleFile,
  onToggleFolder,
  onOpenFolder,
  onSelectFile,
  onPreviewFile,
  onRenameFolder,
  onMoveFolder,
  onDeleteFolder,
  onMoveFile,
}: FileListProps) => {
  const selecting = selectedFiles.length + selectedFolders.length > 0;
  const shownFiles = searching ? searchHits : files;

  if (loading) {
    return (
      <div className={styles.list}>
        <div className={styles.listHeader}>
          <span />
          <span>Name</span>
          <span>Size</span>
          <span>Uploaded</span>
          <span>Uploaded by</span>
          <span />
        </div>
        {[1, 2, 3, 4, 5].map((row) => (
          <div key={row} className={styles.row}>
            <span />
            <div className={styles.skeletonLine} style={{ width: `${50 + row * 6}%` }} />
            <div className={styles.skeletonLine} />
            <div className={styles.skeletonLine} />
            <div className={styles.skeletonLine} />
            <span />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={`${styles.list} ${selecting ? styles.selecting : ""}`}>
      <div className={styles.listHeader}>
        <span />
        <span>Name</span>
        <span>Size</span>
        <span>Uploaded</span>
        <span>Uploaded by</span>
        <span />
      </div>
      {!searching && folders.map((folder) => (
        <div key={folder.id} className={`${styles.folderRow} ${selectedFolders.includes(folder.id) ? styles.rowSelected : ""}`}>
          <input
            type="checkbox"
            checked={selectedFolders.includes(folder.id)}
            onChange={() => onToggleFolder(folder.id)}
            aria-label={`Select ${folder.title}`}
          />
          <div className={styles.fileName}>
            <span className={styles.folderGlyph}><FolderIcon /></span>
            <button type="button" onClick={() => onOpenFolder(folder.id)}>{folder.title}</button>
          </div>
          <span className={styles.meta}>{itemCountLabel(folder)}</span>
          <span />
          <span />
          <div className={styles.rowActions}>
            {canEdit && (
              <FolderMenu
                folderId={folder.id}
                title={folder.title}
                position="left"
                onRename={onRenameFolder}
                onMove={onMoveFolder}
                onDelete={onDeleteFolder}
              />
            )}
          </div>
        </div>
      ))}
      {shownFiles.map((file) => (
        <FileRow
          key={file.id}
          file={file}
          selected={selectedFiles.includes(file.id)}
          active={activeFileId === file.id}
          canMove={canEdit}
          onToggle={onToggleFile}
          onSelect={onSelectFile}
          onPreview={onPreviewFile}
          onMove={onMoveFile}
        />
      ))}
    </div>
  );
};

const FileRow = ({
  file,
  selected,
  active,
  canMove,
  onToggle,
  onSelect,
  onPreview,
  onMove,
}: {
  file: LibraryFileNode;
  selected: boolean;
  active: boolean;
  canMove: boolean;
  onToggle: (id: string) => void;
  onSelect: (file: LibraryFileNode) => void;
  onPreview: (file: LibraryFileNode) => void;
  onMove: (id: string, title: string) => void;
}) => (
  <div
    className={`${styles.row} ${selected ? styles.rowSelected : ""} ${active ? styles.rowActive : ""}`}
    draggable
    onDragStart={(event) => event.dataTransfer.setData("text/file-id", file.id)}
    onClick={() => onSelect(file)}
    onDoubleClick={() => onPreview(file)}
  >
    <input
      type="checkbox"
      checked={selected}
      onClick={(event) => event.stopPropagation()}
      onChange={() => onToggle(file.id)}
      aria-label={`Select ${file.title}`}
    />
    <div className={styles.fileName}>
      <img className={styles.fileIcon} src={getFileIcon(file.title)} alt="" />
      <button type="button" onClick={(event) => { event.stopPropagation(); onSelect(file); }}>{file.title}</button>
      {(file.tags ?? []).length > 0 && (
        <span className={styles.chips}>
          {file.tags.slice(0, 2).map((tag) => <span key={tag} className={styles.chip}>{tag}</span>)}
        </span>
      )}
    </div>
    <span className={styles.meta}>{formatBytes(file.fileSize)}</span>
    <Tooltip text={formatFullDate(file.createdAt) || "Unknown date"} align="top">
      <span className={styles.meta}>{formatRelativeDate(file.createdAt)}</span>
    </Tooltip>
    <span className={styles.meta}>{file.uploadedByName}</span>
    <div className={styles.rowActions}>
      {canMove && <FileMenu fileId={file.id} title={file.title} onMove={onMove} />}
    </div>
  </div>
);

export default FileList;
